import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getUserAccessibleLocations,
  getEtlDlqStats,
} from '../../utils/api';
import { getOrganizations } from '../../utils/organizationApi';

export const ALL_ORGANIZATIONS = '__ALL__';

/**
 * useEtlScope — the organization / location / queue-health scope shared by
 * every ETL Configuration tab.
 *
 * This used to live inside FileTrackingPage, which meant the Data Import and
 * Schema Builder pages each re-derived their own idea of "which locations can
 * this user see" (Data Import read `localStorage.currentOrgId` directly and
 * ignored per-location access entirely). One hook, one answer.
 *
 * The selected org is mirrored into `?org=` so a tab switch — or a link
 * someone pastes into Slack — keeps the scope.
 */
export default function useEtlScope(currentUser, currentOrganization, refreshTrigger = 0) {
  const location = useLocation();
  const navigate = useNavigate();

  const [availableOrgs, setAvailableOrgs] = useState([]);
  // The org list has to load before `selectedOrgCode` can be resolved against
  // it, so the scope is briefly the signed-in org and then the user's full set.
  // Tabs that fetch on scope must wait for this, or they fire twice and the
  // narrower response can land last and win.
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  // The user's explicit choice (from ?org= or the picker). Null means "not
  // chosen", and the effective scope below is derived rather than stored — a
  // stored one had to be corrected by an effect after the org list arrived,
  // which made every consumer fetch once against the interim scope and again
  // against the real one.
  const [orgOverride, setOrgOverride] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dlqStats, setDlqStats] = useState(null);

  // Orgs the user can actually see, in a stable order.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const orgs = await getOrganizations();
        const accessibleCodes = new Set(Object.keys(currentUser?.orgAccess || {}));
        const accessible = (orgs || [])
          .filter((o) => accessibleCodes.has(o.code))
          .sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code));
        if (alive) setAvailableOrgs(accessible);
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        if (alive) setOrgsLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [currentUser]);

  /**
   * The scope in force: an explicit choice wins, then ?org=, then multi-org
   * users default to All, then the signed-in organization. Derived, so it is
   * correct on the first render after the org list resolves instead of being
   * corrected a render later.
   */
  const selectedOrgCode = useMemo(() => {
    const accessibleCodes = new Set(availableOrgs.map((org) => org.code));
    const isUsable = (code) => code === ALL_ORGANIZATIONS || accessibleCodes.has(code);
    if (orgOverride && isUsable(orgOverride)) return orgOverride;
    const urlOrg = new URLSearchParams(location.search).get('org');
    if (urlOrg && isUsable(urlOrg)) return urlOrg;
    if (availableOrgs.length > 1) return ALL_ORGANIZATIONS;
    return currentOrganization?.code || null;
  }, [orgOverride, availableOrgs, currentOrganization?.code, location.search]);

  const setOrgCode = useCallback((orgCode) => {
    setOrgOverride(orgCode);
    const params = new URLSearchParams(location.search);
    if (orgCode) params.set('org', orgCode);
    else params.delete('org');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const orgsToQuery = useMemo(() => (
    selectedOrgCode === ALL_ORGANIZATIONS
      ? availableOrgs.map((org) => org.code).filter(Boolean)
      : [selectedOrgCode || currentOrganization?.code].filter(Boolean)
  ), [selectedOrgCode, availableOrgs, currentOrganization?.code]);

  const orgsKey = orgsToQuery.join(',');

  const locationsRequestId = useRef(0);
  useEffect(() => {
    let alive = true;
    const requestId = ++locationsRequestId.current;
    (async () => {
      try {
        setLocationsLoading(true);
        setError(null);
        // Wait for the org list: resolving against the interim scope costs one
        // request PER ORG and is superseded a moment later by the full set.
        if (!orgsLoaded || !currentUser?.email || !orgsKey) {
          if (alive && orgsLoaded) setLocations([]);
          return;
        }
        const results = await Promise.allSettled(
          orgsKey.split(',').map((code) => getUserAccessibleLocations(currentUser.email, code))
        );
        const all = [];
        for (const r of results) {
          if (r.status === 'fulfilled') all.push(...(r.value?.locations || []));
        }
        // A superseded scope must not overwrite the current one.
        if (alive && locationsRequestId.current === requestId) setLocations(all);
      } catch (err) {
        console.error('Error loading ETL scope locations:', err);
        if (alive && locationsRequestId.current === requestId) setError('Failed to load accessible locations');
      } finally {
        if (alive && locationsRequestId.current === requestId) setLocationsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orgsLoaded, currentUser?.email, orgsKey, refreshTrigger]);

  const accessibleHotelIds = useMemo(() => (
    [...new Set((locations || []).map((loc) => loc.locationId || loc.id).filter(Boolean))]
  ), [locations]);

  const hotelIdsKey = accessibleHotelIds.join(',');

  // When the user has scoped to one organization, pass its id so the server
  // narrows to it; "All organizations" passes nothing and gets their full set.
  const orgIdForScope = useMemo(() => {
    if (selectedOrgCode === ALL_ORGANIZATIONS) return null;
    const match = availableOrgs.find((o) => o.code === selectedOrgCode);
    return match?.id ?? null;
  }, [selectedOrgCode, availableOrgs]);

  const locationNameById = useMemo(() => new Map(
    (locations || []).map((loc) => [loc.locationId || loc.id, loc.name || loc.locationName])
  ), [locations]);

  const locationOptions = useMemo(() => (
    [...locationNameById.entries()]
      .map(([id, name]) => ({ value: String(id), label: name || `Hotel ${id}` }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [locationNameById]);

  /**
   * Queue health — drives the Issues tab badge and its health strip.
   *
   * Sends no hotel list. The server scopes the query to what this user can
   * see; enumerating every accessible hotel here produced a kilobyte-long URL
   * and made the browser the only thing keeping the query in-tenant.
   * `hotelIdsKey` still gates the call so it waits for locations to resolve
   * (an empty scope means there is nothing to ask about yet).
   */
  const statsRequestId = useRef(0);
  const loadDlqStats = useCallback(async () => {
    const requestId = ++statsRequestId.current;
    if (!hotelIdsKey) { setDlqStats(null); return; }
    try {
      const stats = await getEtlDlqStats(orgIdForScope);
      if (statsRequestId.current === requestId) setDlqStats(stats);
    } catch (err) {
      console.error('Failed to load DLQ stats:', err);
    }
  }, [hotelIdsKey, orgIdForScope]);

  useEffect(() => { loadDlqStats(); }, [loadDlqStats, refreshTrigger]);

  const dlqBadgeCount = useMemo(() => {
    const counts = dlqStats?.counts;
    if (!counts) return 0;
    return (counts.dlq || 0) + (counts.failed || 0);
  }, [dlqStats]);

  /**
   * True once the scope is final: the org list has loaded and the locations
   * for it have resolved. Tabs gate their first fetch on this so they issue
   * one request against the settled scope instead of racing two.
   */
  const scopeReady = orgsLoaded && !locationsLoading;

  return {
    availableOrgs,
    orgsLoaded,
    scopeReady,
    selectedOrgCode,
    setOrgCode,
    orgsToQuery,
    orgsKey,
    locations,
    locationsLoading,
    accessibleHotelIds,
    hotelIdsKey,
    locationNameById,
    locationOptions,
    orgIdForScope,
    dlqStats,
    loadDlqStats,
    dlqBadgeCount,
    error,
  };
}
